import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import moment from "moment";
import {
  Box,
  Typography,
  Chip,
  useMediaQuery,
  Collapse,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  ContentCopy,
  CheckCircle,
  Error,
  Warning,
  Help,
  ArrowForward,
  Image,
  ImageNotSupported,
} from "@mui/icons-material";
import BreadcrumbHeader from "layout/BreadcrumbsHeader";
import ThemeBGWrapper from "ui-components/ThemeBGWrapper";
import InviteDealflowModal from "./components/InviteDealflowModal";
import { FetchAnalysisDetail, InviteFromAnalysis } from "api/bulk-analysis";
import { containerRef } from "utils";
import Loader from "../../../common-components/Loader";
import { Primary } from 'ui-components/Buttons';

// ─── Design tokens ────────────────────────────────────────────────────────────
// One disciplined palette. Score-color system is the ONLY place we use red/amber/green.
// Everything else lives in neutrals + the brand action color.

const BRAND = "#381dd4";

const TONE = {
  positive: {
    bg: "#ECFDF3",
    bgDeep: "#DCFAE6",
    fg: "#04342C",
    fgMuted: "#085041",
    dot: "#0F6E56",
    bar: "#1D9E75",
  },
  warning: {
    bg: "#FFF6ED",
    bgDeep: "#FEEED1",
    fg: "#412402",
    fgMuted: "#633806",
    dot: "#BA7517",
    bar: "#EF9F27",
  },
  negative: {
    bg: "#FEF3F2",
    bgDeep: "#FEE4E2",
    fg: "#501313",
    fgMuted: "#791F1F",
    dot: "#A32D2D",
    bar: "#E24B4A",
  },
  info: {
    bg: "#EEF2FF",
    bgDeep: "#E0E7FF",
    fg: "#26215C",
    fgMuted: "#3C3489",
    dot: "#534AB7",
    bar: "#7F77DD",
  },
};

const toneFromScore = (s) =>
  s >= 70 ? TONE.positive : s >= 40 ? TONE.warning : TONE.negative;
const toneFromPct = toneFromScore;
const toneFromSlideScore = (s) =>
  s >= 7 ? TONE.positive : s >= 5 ? TONE.warning : TONE.negative;

const cleanText = (str) =>
  str ? str.replace(/\[id:[a-f0-9]+\]/gi, "").replace(/\s{2,}/g, " ").trim() : str;

const verdictLabel = (score) => {
  if (score >= 80) return "Strong Match";
  if (score >= 70) return "Good Match";
  if (score >= 50) return "Possible Match";
  if (score >= 40) return "Weak Match";
  return "No Match";
};

// ─── Findings synthesis ───────────────────────────────────────────────────────
// We synthesize a flat findings list from the existing API shape so the
// decision-first layout works with the existing backend contract:
//   - pros / cons from backrr_lens  →  positive / negative findings
//   - red_flags from slides          →  warning findings (with slide ref)
// We cap at 6 findings, alternating kinds for visual rhythm.

const synthesizeFindings = (detail) => {
  const pos = [];
  const neg = [];
  const warn = [];

  const lens = detail?.backrr_lens || {};
  if (lens.pros) {
    Object.entries(lens.pros).forEach(([category, items]) => {
      (items || []).forEach((text) => {
        const cleaned = cleanText(text);
        if (cleaned) pos.push({ kind: "positive", text: cleaned, category, slide: null });
      });
    });
  }
  if (lens.cons) {
    Object.entries(lens.cons).forEach(([category, items]) => {
      (items || []).forEach((text) => {
        const cleaned = cleanText(text);
        if (cleaned) neg.push({ kind: "negative", text: cleaned, category, slide: null });
      });
    });
  }
  (detail?.slide_by_slide_analysis || []).forEach((slide) => {
    (slide.red_flags || []).forEach((text) => {
      const cleaned = cleanText(text);
      if (cleaned) warn.push({ kind: "warning", text: cleaned, category: "risk", slide: slide.slide_number });
    });
  });

  const out = [];
  const max = 6;
  let pi = 0;
  let ni = 0;
  let wi = 0;
  while (out.length < max && (pi < pos.length || ni < neg.length || wi < warn.length)) {
    if (pi < pos.length) out.push(pos[pi++]);
    if (out.length >= max) break;
    if (ni < neg.length) out.push(neg[ni++]);
    if (out.length >= max) break;
    if (wi < warn.length) out.push(warn[wi++]);
  }

  // Separate the capped output back into categories for grouping
  const groupedPos = out.filter(f => f.kind === "positive");
  const groupedNeg = out.filter(f => f.kind === "negative");
  const groupedWarn = out.filter(f => f.kind === "warning");

  return { pos: groupedPos, neg: groupedNeg, warn: [] };
};

const splitFinding = (text) => {
  if (!text) return { headline: "", support: null };
  const m = text.match(/^(.+?)(?::\s|\s[—–-]\s)(.+)$/);
  if (m) return { headline: m[1].trim(), support: m[2].trim() };
  return { headline: text, support: null };
};

const ICON_FOR_KIND = {
  positive: CheckCircle,
  negative: Error,
  warning: Warning,
};

// ─── Reusable bits ────────────────────────────────────────────────────────────

const SectionLabel = ({ children, action }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: { xs: 1, sm: 1.25 },
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.68rem", sm: "0.72rem" },
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "#6B7280",
      }}
    >
      {children}
    </Typography>
    {action}
  </Box>
);

const Surface = ({ children, sx, ...props }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      borderRadius: "12px",
      border: "1px solid #EAECF0",
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// ─── Score gauge ──────────────────────────────────────────────────────────────

const ScoreGauge = ({ score, size = 96 }) => {
  const tone = toneFromScore(score);
  const stroke = size <= 80 ? 6 : 8;
  const r = (size - stroke * 2) / 2 + stroke / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.max(0, Math.min(100, score)) / 100) * circ;

  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F2F4F7" strokeWidth={stroke} />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={tone.bar}
          strokeWidth={stroke}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
        />
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: tone.fgMuted,
            lineHeight: 1,
            fontSize: size <= 80 ? "1.4rem" : "1.75rem",
          }}
        >
          {score}
        </Typography>
        <Typography
          sx={{
            color: "#6B7280",
            fontSize: size <= 80 ? "0.625rem" : "0.7rem",
            mt: 0.25,
          }}
        >
          / 100
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Hero shared helpers ──────────────────────────────────────────────────────

const useHeroData = (detail) => {
  const tone = toneFromScore(detail.score);
  const startupName = cleanText(detail?.deck_metadata?.startup_name || detail.startup_name);
  const summary = cleanText(
    detail?.deck_metadata?.one_liner ||
    detail?.deck_metadata?.description ||
    detail?.deck_metadata?.tagline ||
    ""
  );
  const reportLine = detail?.report_name
    ? `${cleanText(detail.report_name)}${detail.batch_created_at ? ` · ${moment(detail.batch_created_at).format("DD-MMM-YYYY")}` : ""}`
    : null;

  const tags = [
    detail?.deck_metadata?.startup_stage,
    detail?.deck_metadata?.startup_category,
    detail?.deck_metadata?.startup_sector,
    detail?.deck_metadata?.startup_business_model,
    detail?.deck_metadata?.startup_geography,
  ].map(cleanText).filter(Boolean);

  return { tone, startupName, summary, reportLine, tags };
};

const MetaTags = ({ tags, tone }) => {
  if (!tags?.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.625, mt: 1.5 }}>
      {tags.map((tag, i) => (
        <Box
          key={i}
          sx={{
            px: 1, py: 0.25, borderRadius: "6px",
            backgroundColor: tone.bg,
            border: `1px solid ${"#E4E7EC"}`,
            // fontSize: "0.68rem", fontWeight: 600,
            // color: i === 0 ? tone.fgMuted : "#475467",
            // whiteSpace: "nowrap",
          }}
        >
          {tag}
        </Box>
      ))}
    </Box>
  );
};

const HeroActions = ({ detail, onInvite, onPass }) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
    <Primary
      onClick={onInvite}
      disabled={detail.invited}
      sx={{
        textTransform: "none",
        px: 2.25,
        py: 1,
        borderRadius: "8px",
        fontSize: "0.875rem",
        fontWeight: 600,
        "&:disabled": {
          color: (theme) => theme.palette.customColors.whiteBG
        }
      }}
    >
      {detail.invited ? "Already Invited" : "Invite to Deal Flow"}
    </Primary>
    {/* {!detail.invited && onPass && (
      <Box
        component="button"
        onClick={onPass}
        sx={{
          background: "transparent", color: "#344054", border: "1px solid #D0D5DD",
          px: 2.25, py: 1, borderRadius: "8px", fontSize: "0.875rem", fontWeight: 600,
          cursor: "pointer", fontFamily: "inherit", "&:hover": { backgroundColor: "#F9FAFB" },
        }}
      >
        Pass
      </Box>
    )} */}
  </Box>
);

// ─── Score Pill Variants ──────────────────────────────────────────────────────

const ScorePillBand = ({ score, tone }) => (
  <Box sx={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(2px)",
    borderRadius: "12px",
    px: 2,
    py: 1.5,
    border: `1.5px solid ${tone.bar}66`,
    flexShrink: 0,
    minWidth: 100,
    boxShadow: `0 2px 8px ${tone.bar}11`,
  }}>
    <Typography sx={{ fontWeight: 800, fontSize: "2rem", color: tone.fgMuted, lineHeight: 1 }}>
      {score}
    </Typography>
    <Typography sx={{ fontSize: "0.6rem", color: tone.dot, fontWeight: 600, mt: 0.25 }}>
      / 100
    </Typography>
    <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: tone.fgMuted, mt: 0.5, whiteSpace: "nowrap", textAlign: "center" }}>
      {verdictLabel(score)}
    </Typography>
  </Box>
);

const ScorePillCompact = ({ score, tone }) => (
  <Box>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: { xs: 1.5, sm: 2 }, py: { xs: 0.875, sm: 1.125 }, backgroundColor: tone.bg, borderRadius: "8px", border: `1px solid ${tone.bar}44`, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.25, lineHeight: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.5em", sm: "1.75em" }, color: tone.fgMuted }}>{score}</Typography>
        <Typography sx={{ fontSize: { xs: "1em", sm: "1.25em" }, color: tone.dot, fontWeight: 700 }}>/ 100</Typography>
      </Box>
    </Box>
    <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: tone.fgMuted, mt: 0.5, whiteSpace: "nowrap", textAlign: "center" }}>
      {verdictLabel(score)}
    </Typography>
  </Box>
);

// ─── Hero: Coloured banner with score pill ───────────────────────────────────

const HeroB = ({ detail, onInvite, onPass, isSmall }) => {
  const { tone, startupName, summary, reportLine, tags } = useHeroData(detail);
  return (
    <Surface sx={{ overflow: "hidden", mb: { xs: 1.5, sm: 2 } }}>
      {/* Coloured top band */}
      <Box sx={{ backgroundColor: tone.bgDeep || tone.bg, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: "1.25rem", sm: "1.6rem" }, fontWeight: 800, color: tone.fg, lineHeight: 1.15, wordBreak: "break-word" }}>
              {startupName}
            </Typography>
            <MetaTags tags={tags} tone={tone} />
          </Box>
          <ScorePillCompact score={detail.score} tone={tone} />
        </Box>
      </Box>
      {/* White content area */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, pb: { xs: "0 !important", md: "1rem !important" }, pt: { xs: "0 !important", md: "1rem !important" } }}>
        {summary && (
          <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "#475467", lineHeight: 1.6, mb: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {summary}
          </Typography>
        )}
        {!isSmall && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <HeroActions detail={detail} onInvite={onInvite} onPass={onPass} />
            {reportLine && <Typography sx={{ ml: "auto", fontSize: "0.75rem", color: "#6B7280" }}>{reportLine}</Typography>}
          </Box>
        )}
      </Box>
    </Surface>
  );
};

// ─── Hero Variant C: Compact scorecard row ────────────────────────────────────

const HeroC = ({ detail, onInvite, onPass, isSmall }) => {
  const { tone, startupName, summary, reportLine, tags } = useHeroData(detail);
  return (
    <Surface sx={{ mb: { xs: 1.5, sm: 2 }, overflow: "hidden" }}>
      {/* Top row: name + score */}
      <Box sx={{ display: "flex", alignItems: "stretch", gap: 0 }}>
        {/* Left: score bar accent */}
        <Box sx={{ width: 5, backgroundColor: tone.bar, flexShrink: 0, borderRadius: "12px 0 0 0" }} />
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.75, sm: 2.25 } }}>
          <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.35rem" }, fontWeight: 800, color: "#101828", lineHeight: 1.2, wordBreak: "break-word" }}>
            {startupName}
          </Typography>
          {summary && (
            <Typography sx={{ fontSize: { xs: "0.78rem", sm: "0.85rem" }, color: "#6B7280", lineHeight: 1.55, mt: 0.75, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {summary}
            </Typography>
          )}
          <MetaTags tags={tags} tone={tone} />
        </Box>
        {/* Right: score block */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 1.5, sm: 2 }, borderLeft: `1px solid ${tone.bar}33`, flexShrink: 0 }}>
          <ScorePillCompact score={detail.score} tone={tone} />
        </Box>
      </Box>
      {/* Bottom action row */}
      {!isSmall && (
        <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: 1.5, borderTop: "1px solid #F2F4F7", display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <HeroActions detail={detail} onInvite={onInvite} onPass={onPass} />
          {reportLine && <Typography sx={{ ml: "auto", fontSize: "0.75rem", color: "#6B7280" }}>{reportLine}</Typography>}
        </Box>
      )}
    </Surface>
  );
};

// ─── Hero switcher ────────────────────────────────────────────────────────────

const HERO_VARIANTS = ["A", "B", "C"];
const HERO_LABELS = { A: "Classic", B: "Banner", C: "Compact" };

const Hero = ({ detail, onInvite, onPass, isSmall }) => (
  <HeroB detail={detail} onInvite={onInvite} onPass={onPass} isSmall={isSmall} />
);

// ─── Findings list ────────────────────────────────────────────────────────────

const FindingRow = ({ finding }) => {
  const toneMap = {
    positive: TONE.positive,
    negative: TONE.negative,
    warning: TONE.warning,
  };
  const tone = toneMap[finding.kind] || TONE.info;
  const Icon = ICON_FOR_KIND[finding.kind] || Help;
  const { headline, support } = splitFinding(finding.text);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.25,
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: "8px",
        backgroundColor: tone.bg,
        alignItems: "flex-start",
      }}
    >
      <Icon sx={{ fontSize: 18, color: tone.dot, flexShrink: 0, mt: 0.125 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              fontWeight: 600,
              color: tone.fg,
              lineHeight: 1.4,
              wordBreak: "break-word",
              flex: 1,
              minWidth: 0,
            }}
          >
            {headline}
          </Typography>
          {finding.slide && (
            <Typography
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                fontWeight: 700,
                color: tone.fgMuted,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {`Slide ${finding.slide}`}
            </Typography>
          )}
        </Box>
        {support && (
          <Typography
            sx={{
              fontSize: { xs: "0.72rem", sm: "0.78rem" },
              color: tone.fgMuted,
              lineHeight: 1.5,
              mt: 0.375,
              wordBreak: "break-word",
            }}
          >
            {support}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const FindingsSection = ({ detail }) => {
  const { pos, neg, warn } = useMemo(() => synthesizeFindings(detail), [detail]);
  const hasFindings = pos.length > 0 || neg.length > 0 || warn.length > 0;
  if (!hasFindings) return null;

  const categories = [
    { label: "Pros", findings: pos, kind: "positive" },
    { label: "Cons", findings: neg, kind: "negative" },
    { label: "Improvements", findings: warn, kind: "warning" },
  ].filter(cat => cat.findings.length > 0);

  return (
    <Surface sx={{ p: { xs: 1.75, sm: 2.5 }, mb: { xs: 1.5, sm: 2 } }}>
      <SectionLabel>Backrr Lens</SectionLabel>
      <Stack spacing={1.75}>
        {categories.map((cat) => (
          <Box key={cat.kind}>
            <Typography
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: TONE[cat.kind].fgMuted,
                mb: 0.875,
              }}
            >
              {cat.label}
            </Typography>
            <Stack spacing={0.875}>
              {cat.findings.map((f, i) => (
                <FindingRow key={i} finding={f} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Surface>
  );
};

// ─── Thesis fit ───────────────────────────────────────────────────────────────

const ThesisCriterionCard = ({ criterion, expanded = true, onToggle }) => {
  const tone = toneFromPct(criterion.match_percentage);
  const confidenceTone = criterion.confidence_level === "high" ? TONE.positive : criterion.confidence_level === "medium" ? TONE.warning : TONE.negative;

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        backgroundColor: "#F2F4F7",
        borderRadius: "8px",
        // cursor: "pointer",
        transition: "background-color 0.15s",
        "&:hover": { backgroundColor: "#F2F4F7" },
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.625, gap: 1, alignItems: "center" }}>
        <Typography
          sx={{
            fontSize: { xs: "0.75rem", sm: "0.8rem" },
            color: "#475467",
            flex: 1,
            minWidth: 0,
            wordBreak: "break-word",
            lineHeight: 1.3,
            fontWeight: 600,
          }}
        >
          {cleanText(criterion.criterion_name)}
        </Typography>
        {criterion?.match_percentage >= 70 ? (
          <CheckCircle sx={{ fontSize: 24, color: "#027A48" }} />
        ) : (
          <Error sx={{ fontSize: 24, color: "#B42318" }} />
        )}
        {/* <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, gap: 0.25 }}>
          <Typography
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              fontWeight: 700,
              color: tone.fgMuted,
            }}
          >
            {criterion.match_percentage}%
          </Typography>
          {criterion.confidence_level && (
            <Typography
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                fontWeight: 600,
                color: confidenceTone.fgMuted,
                textTransform: "capitalize",
              }}
            >
              {criterion.confidence_level}
            </Typography>
          )}
        </Box> */}
      </Box>
      {/* <Box sx={{ height: 4, backgroundColor: "#EAECF0", borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            width: `${Math.max(0, Math.min(100, criterion.match_percentage))}%`,
            height: "100%",
            backgroundColor: tone.bar,
            transition: "width 0.6s ease-out",
          }}
        />
      </Box> */}
      <Collapse in={expanded}>
        <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #EAECF0" }}>
          {criterion.thesis_requirement && (
            <Box sx={{ mb: 0.75 }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                  color: "#6B7280",
                  mb: 0.25,
                }}
              >
                What You Asked For
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  color: "#475467",
                  lineHeight: 1.5,
                  wordBreak: "break-word",
                }}
              >
                {cleanText(criterion.thesis_requirement)}
              </Typography>
            </Box>
          )}
          {criterion.deck_evidence && (
            <Box sx={{ mb: 0.75 }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                  color: "#6B7280",
                  mb: 0.25,
                }}
              >
                What We Found
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  color: "#475467",
                  lineHeight: 1.5,
                  wordBreak: "break-word",
                }}
              >
                {cleanText(criterion.deck_evidence)}
              </Typography>
            </Box>
          )}
          {/* {criterion.evidence_details?.length > 0 && (
            <Box sx={{ mb: 0.75 }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                  color: "#6B7280",
                  mb: 0.25,
                }}
              >
                Details
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}>
                {criterion.evidence_details.map((detail, i) => (
                  <Typography
                    key={i}
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      color: "#6B7280",
                      lineHeight: 1.4,
                      wordBreak: "break-word",
                      pl: 0.75,
                      borderLeft: `2px solid ${tone.bar}`,
                    }}
                  >
                    {cleanText(detail)}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {criterion.notes && (
            <Box
              sx={{
                mt: 0.75,
                p: 0.75,
                backgroundColor: tone.bg,
                borderRadius: "6px",
                borderLeft: `3px solid ${tone.bar}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  color: tone.fg,
                  lineHeight: 1.5,
                  wordBreak: "break-word",
                }}
              >
                {cleanText(criterion.notes)}
              </Typography>
            </Box>
          )} */}
        </Box>
      </Collapse>
    </Box>
  );
};

const ThesisSection = ({ thesis }) => {
  const [expanded, setExpanded] = useState(null);
  if (!thesis) return null;
  const overall = thesis.overall_thesis_match_percentage || 0;
  const tone = toneFromPct(overall);

  // Separate criteria into matched (≥70%) and unmatched (<70%)
  const matched = (thesis.criteria_breakdown || []).filter(cr => (cr.match_percentage || 0) >= 70);
  const unmatched = (thesis.criteria_breakdown || []).filter(cr => (cr.match_percentage || 0) < 70);

  const allArray = [...matched, ...unmatched]

  return (
    <Surface sx={{ p: { xs: 1.75, sm: 2.5 }, mb: { xs: 1.5, sm: 2 } }}>
      <SectionLabel
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {overall >= 70 ? (
              <CheckCircle sx={{ fontSize: 24, color: "#027A48" }} />
            ) : (
              <Error sx={{ fontSize: 24, color: "#B42318" }} />
            )}
            {/* <Box>
              <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" }, fontWeight: 700, color: tone.fgMuted }}>
                {overall}%
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" }, color: "#6B7280" }}>
                match
              </Typography>
            </Box> */}
          </Box>
        }
        
      >
        Thesis fit
      </SectionLabel>

      {thesis.balanced_assessment && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            backgroundColor: TONE.info.bg,
            borderRadius: "8px",
            border: `1px solid ${TONE.info.bar}55`,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "0.65rem", sm: "0.7rem" },
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: TONE.info.fgMuted,
              mb: 0.75,
            }}
          >
            Assessment
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              color: TONE.info.fg,
              lineHeight: 1.6,
              wordBreak: "break-word",
            }}
          >
            {cleanText(thesis.balanced_assessment)}
          </Typography>
        </Box>
      )}

      {allArray.length > 0 && (
        <Box sx={{ mb: 1.75 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: { xs: 1, sm: 1.25 },
            }}
          >
            {allArray.map((cr, i) => (
              <ThesisCriterionCard
                key={`criterion-${i}`}
                criterion={cr}
              // expanded={expanded === `criterion-${i}`}
              // onToggle={() => setExpanded(expanded === `criterion-${i}` ? null : `criterion-${i}`)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Surface>
  );
};

// ─── Ask the founder ──────────────────────────────────────────────────────────

const FounderQuestionsSection = ({ thesis }) => {
  const [copied, setCopied] = useState(false);
  const questions = (thesis?.follow_up_questions || []).map(cleanText).filter(Boolean);
  if (!questions.length) return null;

  const handleCopy = () => {
    const text = questions.map((q, i) => `${i + 1}. ${q}`).join("\n");
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Surface sx={{ p: { xs: 1.75, sm: 2.5 }, mb: { xs: 1.5, sm: 2 } }}>
      <SectionLabel
        action={
          <Tooltip title={copied ? "Copied" : "Copy all"} placement="left">
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{ p: 0.5, color: copied ? TONE.positive.dot : "#6B7280" }}
            >
              <ContentCopy sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        }
      >
        Ask the founder
      </SectionLabel>
      <Stack spacing={0.75}>
        {questions.map((q, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              gap: 1,
              p: { xs: 1.125, sm: 1.25 },
              borderRadius: "8px",
              backgroundColor: "#F9FAFB",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                fontWeight: 700,
                color: "#9CA3AF",
                flexShrink: 0,
                minWidth: 16,
              }}
            >
              {i + 1}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                color: "#344054",
                lineHeight: 1.5,
                wordBreak: "break-word",
              }}
            >
              {q}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Surface>
  );
};

// ─── Slide breakdown (collapsed by default) ──────────────────────────────────

const SlideChip = ({ slide, active, onClick }) => {
  const tone = toneFromSlideScore(slide.quality_score);
  return (
    <Box
      onClick={onClick}
      sx={{
        minWidth: 32,
        width: 32,
        height: 32,
        borderRadius: "8px",
        backgroundColor: active ? tone.bgDeep : tone.bg,
        color: tone.fgMuted,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontSize: "0.75rem",
        fontWeight: 700,
        flexShrink: 0,
        border: active ? `2px solid ${tone.bar}` : "2px solid transparent",
        transition: "all 0.15s",
        "&:hover": { backgroundColor: tone.bgDeep },
      }}
    >
      {slide.slide_number}
    </Box>
  );
};

const SlideDetail = ({ slide }) => {
  const sections = [
    { key: "strengths", tone: TONE.positive, label: "Strengths", icon: CheckCircle },
    { key: "weaknesses", tone: TONE.negative, label: "Weaknesses", icon: Error },
    { key: "red_flags", tone: TONE.warning, label: "Red flags", icon: Warning },
    // { key: "questions_raised", tone: TONE.info, label: "Questions raised", icon: Help },
  ];

  return (
    <Box>
      {slide.content_summary && (
        <Typography
          sx={{
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
            color: "#475467",
            lineHeight: 1.6,
            mb: 1.5,
            wordBreak: "break-word",
          }}
        >
          {cleanText(slide.content_summary)}
        </Typography>
      )}
      {sections.map(({ key, tone, label, icon: Icon }) => {
        const items = (slide[key] || []).map(cleanText).filter(Boolean);
        if (!items?.length) return null;
        return (
          <Box key={key} sx={{ mb: 1.25, "&:last-child": { mb: 0 } }}>
            <Typography
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: tone.fgMuted,
                mb: 0.625,
              }}
            >
              {label}
            </Typography>
            <Stack spacing={0.5}>
              {items.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    gap: 0.875,
                    p: { xs: 0.875, sm: 1 },
                    borderRadius: "6px",
                    backgroundColor: tone.bg,
                  }}
                >
                  <Icon sx={{ fontSize: 14, color: tone.dot, flexShrink: 0, mt: 0.25 }} />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.78rem", sm: "0.825rem" },
                      color: tone.fg,
                      lineHeight: 1.5,
                      wordBreak: "break-word",
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Slide thumbnail ──────────────────────────────────────────────────────────

const SlideThumbnail = ({ slide, slideThumbnails }) => {
  const [imgError, setImgError] = useState(false);
  // slideThumbnails is a 0-based array ordered by page number
  const src = slide.image_url || slideThumbnails?.[slide.slide_number - 1] || null;
  const tone = toneFromSlideScore(slide.quality_score);

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#F9FAFB",
        border: "1px solid #EAECF0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1.5,
        position: "relative",
      }}
    >
      {src && !imgError ? (
        <img
          src={src}
          alt={`Slide ${slide.slide_number}`}
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          onError={() => setImgError(true)}
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
          <ImageNotSupported sx={{ fontSize: 40, color: "#D0D5DD" }} />
          <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>No preview</Typography>
        </Box>
      )}
      {/* Slide number badge */}
      <Box
        sx={{
          position: "absolute",
          bottom: 8,
          left: 8,
          backgroundColor: tone.bg,
          border: `1px solid ${tone.bar}55`,
          borderRadius: "6px",
          px: 0.875,
          py: 0.25,
        }}
      >
        <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: tone.fgMuted }}>
          Slide {slide.slide_number}
        </Typography>
      </Box>
    </Box>
  );
};

const SlideBreakdownSection = ({ slides, slideThumbnails }) => {
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState(0);
  const [showThumb, setShowThumb] = useState(true);
  const [showChips, setShowChips] = useState(false);
  if (!slides?.length) return null;

  const slide = slides[selected];

  return (
    <Surface sx={{ mb: { xs: 1.5, sm: 2 }, overflow: "hidden" }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { backgroundColor: "#F9FAFB" },
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "0.875rem", sm: "0.9375rem" },
              fontWeight: 600,
              color: "#101828",
            }}
          >
            Slide-by-slide breakdown
          </Typography>
          {/* <Typography
            sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" }, color: "#6B7280", mt: 0.125 }}
          >
            {`${slides.length} slides analyzed`}
          </Typography> */}
        </Box>
        {open ? (
          <ExpandLess sx={{ color: "#6B7280" }} />
        ) : (
          <ExpandMore sx={{ color: "#6B7280" }} />
        )}
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
          <Box
            onClick={() => setShowChips(!showChips)}
            sx={{
              display: "flex",
              gap: 1,
              mt: 2,
              mb: 2,
              p: 1.25,
              backgroundColor: "#F9FAFB",
              borderRadius: "8px",
              cursor: "pointer",
              userSelect: "none",
              alignItems: "flex-start",
              justifyContent: showChips ? "flex-start" : "space-between",
            }}
          >
            <Box sx={{ display: "grid", gap: 0.625, flexWrap: "wrap", flex: 1, alignItems: "flex-start" }}>
              <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", fontWeight: 500 }}>
                {slides.length} slides
              </Typography>
              {showChips ? (
                <Box sx={{ display: "flex", gap: 0.625, flexWrap: "wrap", flex: 1 }}>
                  {slides.map((s, i) => (
                    <SlideChip
                      key={i}
                      slide={s}
                      active={selected === i}
                      onClick={(e) => { e.stopPropagation(); setSelected(i); }}
                    />
                  ))}
                </Box>
              ) : (
                <></>
              )}
            </Box>
            {showChips ? (
              <ExpandLess sx={{ color: "#6B7280", flexShrink: 0 }} />
            ) : (
              <ExpandMore sx={{ color: "#6B7280", flexShrink: 0 }} />
            )}
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                mb: 1.5,
                pb: 1.5,
                borderBottom: "1px solid #EAECF0",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  backgroundColor: toneFromSlideScore(slide.quality_score).bg,
                  color: toneFromSlideScore(slide.quality_score).fgMuted,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  flexShrink: 0,
                }}
              >
                {slide.slide_number}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                    fontWeight: 600,
                    color: "#101828",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  {cleanText(slide.slide_title) || `Slide ${slide.slide_number}`}
                </Typography>
                {/* <Typography
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    color: "#6B7280",
                    mt: 0.125,
                  }}
                >
                  {cleanText(slide.slide_type)}
                </Typography> */}
              </Box>
              {/* <Chip
                label={`${slide.quality_score}/10`}
                size="small"
                sx={{
                  backgroundColor: toneFromSlideScore(slide.quality_score).bg,
                  color: toneFromSlideScore(slide.quality_score).fgMuted,
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  height: 22,
                  flexShrink: 0,
                }}
              /> */}
              <Tooltip title={showThumb ? "Hide thumbnail" : "Show thumbnail"} placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setShowThumb((v) => !v); }}
                  sx={{ p: 0.5, color: showThumb ? BRAND : "#9CA3AF", flexShrink: 0 }}
                >
                  {showThumb ? <Image sx={{ fontSize: 18 }} /> : <ImageNotSupported sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            </Box>

            {showThumb && (
              <SlideThumbnail
                key={`${selected}-${slide.slide_number}`}
                slide={slide}
                slideThumbnails={slideThumbnails}
              />
            )}

            <SlideDetail slide={slide} />
            {slides.length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                  pt: 1.5,
                  borderTop: "1px solid #EAECF0",
                }}
              >
                <Box
                  onClick={() => setSelected((p) => Math.max(0, p - 1))}
                  sx={{
                    cursor: selected > 0 ? "pointer" : "default",
                    opacity: selected > 0 ? 1 : 0.3,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: BRAND,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    minHeight: 36,
                    userSelect: "none",
                  }}
                >
                  <ArrowForward sx={{ fontSize: 14, transform: "rotate(180deg)" }} />
                  Previous
                </Box>
                <Box
                  onClick={() => setSelected((p) => Math.min(slides.length - 1, p + 1))}
                  sx={{
                    cursor: selected < slides.length - 1 ? "pointer" : "default",
                    opacity: selected < slides.length - 1 ? 1 : 0.3,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: BRAND,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    minHeight: 36,
                    userSelect: "none",
                  }}
                >
                  Next
                  <ArrowForward sx={{ fontSize: 14 }} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
    </Surface>
  );
};

// ─── Mobile sticky bottom action bar ──────────────────────────────────────────

const MobileActionBar = ({ detail, onInvite, onPass }) => (
  <Box
    sx={{
      position: "sticky",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      mt: 1,
      mx: -0.625,
      px: 1.25,
      pt: 1.25,
      pb: "calc(1.25rem + env(safe-area-inset-bottom, 0px))",
      backgroundColor: "rgba(255,255,255,0.96)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      borderTop: "1px solid #EAECF0",
      display: "flex",
      gap: 1,
    }}
  >
    {/* {!detail.invited && (
      <Box
        component="button"
        onClick={onPass}
        sx={{
          background: "transparent",
          color: "#344054",
          border: "1px solid #D0D5DD",
          px: 2,
          py: 1.25,
          borderRadius: "8px",
          fontSize: "0.875rem",
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "inherit",
          flexShrink: 0,
        }}
      >
        Pass
      </Box>
    )} */}
    <Primary
      onClick={onInvite}
      disabled={detail.invited}
      sx={{
        textTransform: "none",
        flex: 1,
        py: 1.25,
        borderRadius: "8px",
        fontSize: "0.875rem",
        fontWeight: 600,
        "&:disabled": {
          color: (theme) => theme.palette.customColors.whiteBG
        }
      }}
    >
      {detail.invited ? "Already invited" : "Invite to Deal Flow"}
    </Primary>
  </Box>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const AnalysisDetailPage = () => {
  const { file_id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { data: detail, isLoading, refetch } = FetchAnalysisDetail(file_id);
  const inviteMutation = InviteFromAnalysis();
  const [inviteModal, setInviteModal] = useState({ open: false });

  console.log(isSmallScreen, "isSmallScreen");

  if (isLoading)
    return (
      <Loader
        sx={{
          ...(isSmallScreen
            ? {}
            : {
              top: "1.25rem",
              maxHeight: "calc(100dvh - 1.25rem)",
              borderRadius: "24px 0 0 0",
              position: "absolute",
              right: "-2px",
              width: "calc(100% - 5rem)",
            }),
        }}
      />
    );

  if (!detail) {
    return (
      <ThemeBGWrapper>
        <BreadcrumbHeader
          title="Analysis detail"
          breadcrumbItems={[
            { title: "Tools", path: "" },
            { title: "All Results", path: "/bulk-analysis" },
          ]}
        />
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ color: "#6B7280" }}>No analysis found</Typography>
        </Box>
      </ThemeBGWrapper>
    );
  }

  const startupName =
    detail?.deck_metadata?.startup_name || detail.startup_name || "Detail";
  const onInvite = () => setInviteModal({ open: true });
  const onPass = () => {
    // Hook for "pass" action — wire to a real "mark as passed" API when ready.
    // For now navigates back to the list.
    navigate("/bulk-analysis");
  };

  const handleInviteSend = async (result) => {
    try {
      await inviteMutation.mutateAsync({
        batch_id: detail.batch_id,
        file_id: detail.file_id,
        email: result.email,
        startup_name: result.startup_name,
        message: result.message,
      });
      setInviteModal({ open: false });
      await refetch();
    } catch (err) {
      console.error("Failed to send invite:", err);
    }
  };

  return (
    <ThemeBGWrapper>
      <BreadcrumbHeader
        title={`${startupName}${detail?.report_name ? ` · ${detail.report_name}` : ""}`}
        breadcrumbItems={[
          { title: "Tools", path: "" },
          { title: "All Results", path: "/bulk-analysis" },
        ]}
        sx={{ paddingBottom: "0.5rem !important" }}
      />

      <Box
        sx={{
          padding: {
            xs: "0 0.625rem 0",
            sm: "0 1rem 1rem",
            md: "0 2rem 2rem",
          },
        }}
      >
        <Box
          containerRef={containerRef}
          sx={{
            height: {
              xs: "calc(100dvh - 4rem)",
              sm: "calc(100dvh - 4rem)",
              md: "calc(100dvh - 6.5rem)",
            },
            overflow: "auto",
            paddingRight: { xs: 0, sm: "0.75rem" },
            scrollbarWidth: { xs: "none", sm: "thin" },
            "&::-webkit-scrollbar": {
              display: { xs: "none", sm: "block" },
            },
            WebkitOverflowScrolling: "touch",
            position: "relative",
          }}
        >
          <Box sx={{ maxWidth: 880, mx: "auto" }}>
            <Hero
              detail={detail}
              onInvite={onInvite}
              onPass={onPass}
              isSmall={isSmallScreen}
            />
            <FindingsSection detail={detail} />
            <ThesisSection thesis={{ ...detail?.thesis_match_breakdown, balanced_assessment: detail?.backrr_lens?.balanced_assessment }} />
            <SlideBreakdownSection slides={detail.slide_by_slide_analysis} slideThumbnails={detail.slide_thumbnails || []} />
            <FounderQuestionsSection thesis={detail.thesis_match_breakdown} />
            <Box sx={{ height: { xs: 8, sm: 16 } }} />
          </Box>

          {isSmallScreen && (
            <MobileActionBar detail={detail} onInvite={onInvite} onPass={onPass} />
          )}
        </Box>
      </Box>

      <InviteDealflowModal
        open={inviteModal.open}
        onClose={() => setInviteModal({ open: false })}
        result={detail}
        onSend={handleInviteSend}
        isLoading={inviteMutation.isLoading}
      />
    </ThemeBGWrapper>
  );
};

export default AnalysisDetailPage;